import { useEffect, useState } from "react";
import { api, LOGO_URL } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../components/ui/tabs";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
    Table,
    TableHeader,
    TableRow,
    TableHead,
    TableBody,
    TableCell,
} from "../components/ui/table";
import { Plus, Trash2, Edit, LogOut, Lock, X, Search, ArrowUpDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { resizeImageToDataUrl } from "../lib/utils-extra";

const emptyProduct = {
    name: "",
    category: "flower",
    tier: "Tier 1",
    description: "",
    image_url: "",
    coa_url: "",
    variants: [{ label: "1/8 oz", price: 25 }],
    featured: false,
};

const SORT_OPTIONS = [
    { value: "created_desc", label: "Newest first" },
    { value: "created_asc", label: "Oldest first" },
    { value: "name_asc", label: "Name A→Z" },
    { value: "name_desc", label: "Name Z→A" },
    { value: "tier", label: "Tier" },
    { value: "category", label: "Category" },
];

export default function AdminPage() {
    const [token, setToken] = useState(
        () => localStorage.getItem("high95_admin_token") || ""
    );
    const [password, setPassword] = useState("");
    const [products, setProducts] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("created_desc");
    const [uploading, setUploading] = useState(false);
    const [announcement, setAnnouncement] = useState({
        active: false,
        title: "",
        body: "",
        cta_text: "",
        cta_url: "",
    });
    const [annSaving, setAnnSaving] = useState(false);

    const authHeaders = { Authorization: `Bearer ${token}` };

    const login = async () => {
        try {
            const { data } = await api.post("/admin/login", { password });
            localStorage.setItem("high95_admin_token", data.token);
            setToken(data.token);
            toast.success("Logged in.");
        } catch (e) {
            toast.error("Wrong password.");
        }
    };

    const logout = () => {
        localStorage.removeItem("high95_admin_token");
        setToken("");
        setProducts([]);
        setContacts([]);
    };

    const loadAll = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const [p, c, a] = await Promise.all([
                api.get("/products"),
                api.get("/admin/contacts", { headers: authHeaders }),
                api.get("/announcement"),
            ]);
            setProducts(p.data);
            setContacts(c.data);
            if (a.data) {
                setAnnouncement({
                    active: !!a.data.active,
                    title: a.data.title || "",
                    body: a.data.body || "",
                    cta_text: a.data.cta_text || "",
                    cta_url: a.data.cta_url || "",
                });
            }
        } catch (e) {
            if (e.response?.status === 401) {
                toast.error("Session expired.");
                logout();
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const openNew = () => {
        setEditingProduct({ ...emptyProduct });
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditingProduct(JSON.parse(JSON.stringify(p)));
        setDialogOpen(true);
    };

    const saveProduct = async () => {
        if (!editingProduct.name) {
            toast.error("Name required");
            return;
        }
        try {
            const payload = {
                ...editingProduct,
                variants: editingProduct.variants.map((v) => ({
                    label: v.label,
                    price: Number(v.price),
                })),
            };
            if (editingProduct.id) {
                await api.put(`/admin/products/${editingProduct.id}`, payload, {
                    headers: authHeaders,
                });
                toast.success("Updated.");
            } else {
                await api.post("/admin/products", payload, {
                    headers: authHeaders,
                });
                toast.success("Created.");
            }
            setDialogOpen(false);
            setEditingProduct(null);
            loadAll();
        } catch (e) {
            toast.error("Save failed.");
        }
    };

    const deleteProduct = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        try {
            await api.delete(`/admin/products/${id}`, { headers: authHeaders });
            toast.success("Deleted.");
            loadAll();
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Max 8MB per image.");
            return;
        }
        setUploading(true);
        try {
            const dataUrl = await resizeImageToDataUrl(file, 1024, 0.85);
            setEditingProduct((p) => ({ ...p, image_url: dataUrl }));
            toast.success("Image attached.");
        } catch {
            toast.error("Couldn't process image.");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const saveAnnouncement = async () => {
        setAnnSaving(true);
        try {
            const { data } = await api.put(
                "/admin/announcement",
                announcement,
                { headers: authHeaders }
            );
            setAnnouncement({
                active: !!data.active,
                title: data.title || "",
                body: data.body || "",
                cta_text: data.cta_text || "",
                cta_url: data.cta_url || "",
            });
            toast.success(
                announcement.active
                    ? "Drop banner is live."
                    : "Drop banner saved (inactive)."
            );
        } catch (e) {
            toast.error("Couldn't save announcement.");
        } finally {
            setAnnSaving(false);
        }
    };

    const visibleProducts = (() => {
        let list = [...products];
        const q = searchQuery.trim().toLowerCase();
        if (q) {
            list = list.filter(
                (p) =>
                    p.name.toLowerCase().includes(q) ||
                    (p.tier || "").toLowerCase().includes(q) ||
                    p.category.toLowerCase().includes(q) ||
                    (p.description || "").toLowerCase().includes(q)
            );
        }
        list.sort((a, b) => {
            switch (sortBy) {
                case "created_asc":
                    return (
                        new Date(a.created_at).getTime() -
                        new Date(b.created_at).getTime()
                    );
                case "name_asc":
                    return a.name.localeCompare(b.name);
                case "name_desc":
                    return b.name.localeCompare(a.name);
                case "tier":
                    return (a.tier || "").localeCompare(b.tier || "");
                case "category":
                    return a.category.localeCompare(b.category);
                case "created_desc":
                default:
                    return (
                        new Date(b.created_at).getTime() -
                        new Date(a.created_at).getTime()
                    );
            }
        });
        return list;
    })();

    if (!token) {
        return (
            <div
                data-testid="admin-login"
                className="min-h-screen flex items-center justify-center px-4"
            >
                <div className="glass rounded-3xl p-8 max-w-md w-full grain relative overflow-hidden">
                    <img
                        src={LOGO_URL}
                        alt="High 95"
                        className="w-16 h-16 mx-auto mb-5 rounded-xl object-cover ring-1 ring-white/10"
                    />
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Lock className="w-4 h-4 text-highway-red" />
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-highway-red">
                            Restricted Lane
                        </p>
                    </div>
                    <h1 className="font-display text-3xl font-bold text-white text-center mb-6">
                        Admin Login
                    </h1>
                    <div className="space-y-4">
                        <div>
                            <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                Password
                            </Label>
                            <Input
                                data-testid="admin-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && login()}
                                className="bg-background/60 border-border text-white h-12 mt-2"
                                placeholder="Enter admin password"
                            />
                        </div>
                        <Button
                            data-testid="admin-login-btn"
                            onClick={login}
                            className="w-full rounded-full bg-highway-red hover:bg-highway-red/90 text-white py-6 font-semibold glow-red"
                        >
                            Enter
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-dashboard" className="min-h-screen">
            {/* Top bar */}
            <div className="glass border-b border-white/10 sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src={LOGO_URL}
                            alt=""
                            className="w-10 h-10 rounded-xl object-cover ring-1 ring-white/10"
                        />
                        <div>
                            <p className="font-display font-bold text-white text-lg">
                                High95 · Admin
                            </p>
                            <p className="font-mono text-[10px] text-white/50 uppercase tracking-widest">
                                Mile Marker 95 · Control Tower
                            </p>
                        </div>
                    </div>
                    <Button
                        data-testid="admin-logout-btn"
                        onClick={logout}
                        variant="outline"
                        className="rounded-full border-white/20 text-white hover:bg-white/10 hover:text-white"
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                    </Button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
                <Tabs defaultValue="products">
                    <TabsList className="bg-card border border-border rounded-full p-1 h-auto mb-8">
                        <TabsTrigger
                            value="products"
                            data-testid="tab-products"
                            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-black text-white/70"
                        >
                            Products ({products.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="drop"
                            data-testid="tab-drop"
                            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-black text-white/70"
                        >
                            Today's Drop
                            {announcement.active && (
                                <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            value="contacts"
                            data-testid="tab-contacts"
                            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-black text-white/70"
                        >
                            Messages ({contacts.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="products">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                            <h2 className="font-display text-2xl font-bold text-white">
                                Products
                            </h2>
                            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <Input
                                        data-testid="admin-search-input"
                                        value={searchQuery}
                                        onChange={(e) =>
                                            setSearchQuery(e.target.value)
                                        }
                                        placeholder="Search products..."
                                        className="pl-9 bg-card border-border text-white h-10 w-full sm:w-56"
                                    />
                                </div>
                                <Select
                                    value={sortBy}
                                    onValueChange={setSortBy}
                                >
                                    <SelectTrigger
                                        data-testid="admin-sort-trigger"
                                        className="bg-card border-border text-white h-10 w-full sm:w-44"
                                    >
                                        <ArrowUpDown className="w-3.5 h-3.5 mr-2" />
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-card border-border text-white">
                                        {SORT_OPTIONS.map((s) => (
                                            <SelectItem
                                                key={s.value}
                                                value={s.value}
                                            >
                                                {s.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button
                                    data-testid="new-product-btn"
                                    onClick={openNew}
                                    className="rounded-full bg-highway-red hover:bg-highway-red/90 text-white whitespace-nowrap"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    New product
                                </Button>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-border bg-card overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-border hover:bg-transparent">
                                        <TableHead className="text-white/60 font-mono text-xs uppercase tracking-widest">
                                            Image
                                        </TableHead>
                                        <TableHead className="text-white/60 font-mono text-xs uppercase tracking-widest">
                                            Name
                                        </TableHead>
                                        <TableHead className="text-white/60 font-mono text-xs uppercase tracking-widest">
                                            Category
                                        </TableHead>
                                        <TableHead className="text-white/60 font-mono text-xs uppercase tracking-widest">
                                            Tier
                                        </TableHead>
                                        <TableHead className="text-white/60 font-mono text-xs uppercase tracking-widest">
                                            Variants
                                        </TableHead>
                                        <TableHead className="text-right text-white/60 font-mono text-xs uppercase tracking-widest">
                                            Actions
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {visibleProducts.map((p) => (
                                        <TableRow
                                            key={p.id}
                                            className="border-border"
                                            data-testid={`admin-row-${p.id}`}
                                        >
                                            <TableCell>
                                                <img
                                                    src={p.image_url}
                                                    alt=""
                                                    className="w-12 h-12 rounded-lg object-cover"
                                                />
                                            </TableCell>
                                            <TableCell className="text-white font-medium">
                                                {p.name}
                                                {p.featured && (
                                                    <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-highway-red">
                                                        ★ feat
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-white/70 capitalize">
                                                {p.category}
                                            </TableCell>
                                            <TableCell className="text-white/70 font-mono text-sm">
                                                {p.tier || "—"}
                                            </TableCell>
                                            <TableCell className="text-white/70 font-mono text-xs">
                                                {p.variants
                                                    .map(
                                                        (v) =>
                                                            `${v.label}/$${v.price}`
                                                    )
                                                    .join(" · ")}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    data-testid={`edit-${p.id}`}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => openEdit(p)}
                                                    className="text-white/70 hover:text-white hover:bg-white/10"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    data-testid={`delete-${p.id}`}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() =>
                                                        deleteProduct(p.id)
                                                    }
                                                    className="text-highway-red hover:text-highway-red hover:bg-highway-red/10"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {visibleProducts.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                className="text-center text-white/40 py-10"
                                            >
                                                {searchQuery
                                                    ? `No products match "${searchQuery}".`
                                                    : "No products yet."}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="drop">
                        <h2 className="font-display text-2xl font-bold text-white mb-2">
                            Today's Drop
                        </h2>
                        <p className="text-white/60 text-sm mb-6 max-w-2xl">
                            A dismissible banner that shows above the menu — use
                            it for restocks, weekend deals, or new strain
                            announcements.
                        </p>
                        <div className="rounded-2xl bg-card border border-border p-6 sm:p-8 max-w-2xl space-y-5">
                            <div className="flex items-center justify-between p-4 rounded-xl bg-background/60 border border-border">
                                <div>
                                    <p className="text-white font-medium">
                                        Show banner on site
                                    </p>
                                    <p className="text-xs text-white/50 mt-1">
                                        When off, visitors won't see anything.
                                    </p>
                                </div>
                                <Switch
                                    data-testid="drop-active-switch"
                                    checked={announcement.active}
                                    onCheckedChange={(c) =>
                                        setAnnouncement({
                                            ...announcement,
                                            active: c,
                                        })
                                    }
                                />
                            </div>
                            <div>
                                <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                    Headline
                                </Label>
                                <Input
                                    data-testid="drop-title-input"
                                    value={announcement.title}
                                    onChange={(e) =>
                                        setAnnouncement({
                                            ...announcement,
                                            title: e.target.value,
                                        })
                                    }
                                    className="bg-background/60 border-border h-11 mt-2"
                                    placeholder="Fresh restock — Tier 1 just landed"
                                />
                            </div>
                            <div>
                                <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                    Subtext{" "}
                                    <span className="opacity-50">
                                        (optional)
                                    </span>
                                </Label>
                                <Textarea
                                    data-testid="drop-body-input"
                                    value={announcement.body}
                                    onChange={(e) =>
                                        setAnnouncement({
                                            ...announcement,
                                            body: e.target.value,
                                        })
                                    }
                                    rows={2}
                                    className="bg-background/60 border-border mt-2"
                                    placeholder="Same-day delivery in South Jersey while supplies last."
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                        CTA text{" "}
                                        <span className="opacity-50">(opt)</span>
                                    </Label>
                                    <Input
                                        data-testid="drop-cta-text-input"
                                        value={announcement.cta_text}
                                        onChange={(e) =>
                                            setAnnouncement({
                                                ...announcement,
                                                cta_text: e.target.value,
                                            })
                                        }
                                        className="bg-background/60 border-border h-11 mt-2"
                                        placeholder="Shop now"
                                    />
                                </div>
                                <div>
                                    <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                        CTA link{" "}
                                        <span className="opacity-50">(opt)</span>
                                    </Label>
                                    <Input
                                        data-testid="drop-cta-url-input"
                                        value={announcement.cta_url}
                                        onChange={(e) =>
                                            setAnnouncement({
                                                ...announcement,
                                                cta_url: e.target.value,
                                            })
                                        }
                                        className="bg-background/60 border-border h-11 mt-2"
                                        placeholder="#menu or https://t.me/highnintyfive"
                                    />
                                </div>
                            </div>

                            {/* Live preview */}
                            <div className="pt-4 border-t border-border">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">
                                    Preview
                                </p>
                                {announcement.title ? (
                                    <div className="rounded-xl border border-highway-red/30 bg-gradient-to-r from-highway-red/15 via-highway-red/5 to-transparent p-4 flex items-center gap-3">
                                        <div className="shrink-0 w-9 h-9 rounded-full bg-highway-red/15 border border-highway-red/40 flex items-center justify-center">
                                            <Plus className="w-4 h-4 text-highway-red" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-highway-red">
                                                // Today's Drop
                                            </p>
                                            <p className="font-display font-semibold text-white text-sm mt-1">
                                                {announcement.title}
                                                {announcement.body && (
                                                    <span className="font-sans font-normal text-white/70 ml-2">
                                                        {announcement.body}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                        {announcement.cta_text && (
                                            <span className="px-3 py-1.5 rounded-full bg-white text-black text-xs font-semibold whitespace-nowrap">
                                                {announcement.cta_text}
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-white/40 text-sm italic">
                                        Add a headline to see the preview.
                                    </p>
                                )}
                            </div>

                            <Button
                                data-testid="drop-save-btn"
                                onClick={saveAnnouncement}
                                disabled={annSaving}
                                className="rounded-full bg-highway-red hover:bg-highway-red/90 text-white font-semibold px-6"
                            >
                                {annSaving ? "Saving..." : "Save & publish"}
                            </Button>
                        </div>
                    </TabsContent>

                    <TabsContent value="contacts">
                        <h2 className="font-display text-2xl font-bold text-white mb-6">
                            Messages
                        </h2>
                        <div className="space-y-3">
                            {contacts.map((c) => (
                                <div
                                    key={c.id}
                                    data-testid={`contact-row-${c.id}`}
                                    className="rounded-2xl bg-card border border-border p-5"
                                >
                                    <div className="flex justify-between items-start gap-4 mb-2">
                                        <div>
                                            <p className="font-display font-semibold text-white">
                                                {c.name}
                                            </p>
                                            <p className="font-mono text-xs text-white/50">
                                                {c.phone}{" "}
                                                {c.email && `· ${c.email}`}
                                            </p>
                                        </div>
                                        <p className="font-mono text-[10px] text-white/40 uppercase tracking-widest whitespace-nowrap">
                                            {new Date(
                                                c.created_at
                                            ).toLocaleString()}
                                        </p>
                                    </div>
                                    <p className="text-white/70 text-sm whitespace-pre-wrap">
                                        {c.message}
                                    </p>
                                </div>
                            ))}
                            {contacts.length === 0 && (
                                <div className="text-center text-white/40 py-10 font-mono uppercase text-sm tracking-widest">
                                    No messages yet.
                                </div>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Product editor dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent
                    data-testid="product-dialog"
                    className="bg-card border-border text-white max-w-2xl max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            {editingProduct?.id
                                ? "Edit product"
                                : "New product"}
                        </DialogTitle>
                        <DialogDescription className="text-white/60 text-sm">
                            Manage product details, image, lab report, and
                            pricing variants.
                        </DialogDescription>
                    </DialogHeader>
                    {editingProduct && (
                        <div className="space-y-5 mt-4">
                            <div>
                                <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                    Name
                                </Label>
                                <Input
                                    data-testid="form-name"
                                    value={editingProduct.name}
                                    onChange={(e) =>
                                        setEditingProduct({
                                            ...editingProduct,
                                            name: e.target.value,
                                        })
                                    }
                                    className="bg-background/60 border-border h-11 mt-2"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                        Category
                                    </Label>
                                    <Select
                                        value={editingProduct.category}
                                        onValueChange={(v) =>
                                            setEditingProduct({
                                                ...editingProduct,
                                                category: v,
                                            })
                                        }
                                    >
                                        <SelectTrigger
                                            data-testid="form-category"
                                            className="bg-background/60 border-border h-11 mt-2"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-card border-border text-white">
                                            <SelectItem value="flower">
                                                Flower
                                            </SelectItem>
                                            <SelectItem value="concentrate">
                                                Concentrate
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                        Tier
                                    </Label>
                                    <Input
                                        data-testid="form-tier"
                                        value={editingProduct.tier || ""}
                                        onChange={(e) =>
                                            setEditingProduct({
                                                ...editingProduct,
                                                tier: e.target.value,
                                            })
                                        }
                                        className="bg-background/60 border-border h-11 mt-2"
                                        placeholder="Tier 1, Badder, etc."
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                    Product Image
                                </Label>
                                <div className="mt-2 space-y-2">
                                    {editingProduct.image_url && (
                                        <div className="relative w-full h-44 rounded-xl overflow-hidden border border-border bg-background/60">
                                            <img
                                                src={editingProduct.image_url}
                                                alt="Preview"
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                data-testid="form-clear-image"
                                                type="button"
                                                onClick={() =>
                                                    setEditingProduct({
                                                        ...editingProduct,
                                                        image_url: "",
                                                    })
                                                }
                                                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white"
                                                title="Remove image"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex gap-2">
                                        <label
                                            data-testid="form-upload-label"
                                            className={`flex-1 flex items-center justify-center gap-2 cursor-pointer rounded-full border border-dashed border-white/20 hover:border-white/40 hover:bg-white/5 px-4 h-11 text-sm text-white/70 transition ${
                                                uploading
                                                    ? "opacity-50 pointer-events-none"
                                                    : ""
                                            }`}
                                        >
                                            <Upload className="w-4 h-4" />
                                            {uploading
                                                ? "Processing..."
                                                : "Upload image"}
                                            <input
                                                data-testid="form-upload-input"
                                                type="file"
                                                accept="image/*"
                                                onChange={handleUpload}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                    <Input
                                        data-testid="form-image"
                                        value={
                                            editingProduct.image_url?.startsWith(
                                                "data:"
                                            )
                                                ? ""
                                                : editingProduct.image_url
                                        }
                                        onChange={(e) =>
                                            setEditingProduct({
                                                ...editingProduct,
                                                image_url: e.target.value,
                                            })
                                        }
                                        className="bg-background/60 border-border h-11"
                                        placeholder="…or paste an image URL"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                    Lab Report (COA) URL{" "}
                                    <span className="opacity-50">
                                        (optional)
                                    </span>
                                </Label>
                                <Input
                                    data-testid="form-coa"
                                    value={editingProduct.coa_url || ""}
                                    onChange={(e) =>
                                        setEditingProduct({
                                            ...editingProduct,
                                            coa_url: e.target.value,
                                        })
                                    }
                                    className="bg-background/60 border-border h-11 mt-2"
                                    placeholder="https://link-to-coa.pdf"
                                />
                            </div>
                            <div>
                                <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                    Description
                                </Label>
                                <Textarea
                                    data-testid="form-description"
                                    value={editingProduct.description}
                                    onChange={(e) =>
                                        setEditingProduct({
                                            ...editingProduct,
                                            description: e.target.value,
                                        })
                                    }
                                    rows={3}
                                    className="bg-background/60 border-border mt-2"
                                />
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-3">
                                    <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                                        Variants
                                    </Label>
                                    <Button
                                        data-testid="form-add-variant"
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                            setEditingProduct({
                                                ...editingProduct,
                                                variants: [
                                                    ...editingProduct.variants,
                                                    { label: "", price: 0 },
                                                ],
                                            })
                                        }
                                        className="rounded-full border-white/20 text-white hover:bg-white/10 hover:text-white"
                                    >
                                        <Plus className="w-3 h-3 mr-1" />
                                        Add
                                    </Button>
                                </div>
                                <div className="space-y-2">
                                    {editingProduct.variants.map((v, i) => (
                                        <div
                                            key={i}
                                            className="flex gap-2 items-center"
                                        >
                                            <Input
                                                data-testid={`form-variant-label-${i}`}
                                                placeholder="1/8 oz"
                                                value={v.label}
                                                onChange={(e) => {
                                                    const next = [
                                                        ...editingProduct.variants,
                                                    ];
                                                    next[i].label =
                                                        e.target.value;
                                                    setEditingProduct({
                                                        ...editingProduct,
                                                        variants: next,
                                                    });
                                                }}
                                                className="bg-background/60 border-border h-10 flex-1"
                                            />
                                            <Input
                                                data-testid={`form-variant-price-${i}`}
                                                placeholder="25"
                                                type="number"
                                                value={v.price}
                                                onChange={(e) => {
                                                    const next = [
                                                        ...editingProduct.variants,
                                                    ];
                                                    next[i].price =
                                                        e.target.value;
                                                    setEditingProduct({
                                                        ...editingProduct,
                                                        variants: next,
                                                    });
                                                }}
                                                className="bg-background/60 border-border h-10 w-28 font-mono"
                                            />
                                            <Button
                                                data-testid={`form-remove-variant-${i}`}
                                                type="button"
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => {
                                                    setEditingProduct({
                                                        ...editingProduct,
                                                        variants:
                                                            editingProduct.variants.filter(
                                                                (_, j) =>
                                                                    j !== i
                                                            ),
                                                    });
                                                }}
                                                className="text-highway-red hover:bg-highway-red/10 hover:text-highway-red"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center justify-between p-4 rounded-xl bg-background/60 border border-border">
                                <div>
                                    <p className="text-white font-medium">
                                        Featured
                                    </p>
                                    <p className="text-xs text-white/50">
                                        Pin to top of menu with a badge
                                    </p>
                                </div>
                                <Switch
                                    data-testid="form-featured"
                                    checked={editingProduct.featured}
                                    onCheckedChange={(c) =>
                                        setEditingProduct({
                                            ...editingProduct,
                                            featured: c,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter className="mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setDialogOpen(false)}
                            className="rounded-full border-white/20 text-white hover:bg-white/10 hover:text-white"
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="form-save"
                            onClick={saveProduct}
                            className="rounded-full bg-highway-red hover:bg-highway-red/90 text-white"
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
